type MapboxEndpointFamily =
  | 'geocode'
  | 'search'
  | 'searchBox'
  | 'tilequery'
  | 'directions'
  | 'optimization'
  | 'matrix'
  | 'reverseGeocode';

type BudgetSpec = {
  perMinute: number;
  perSession: number;
};

type EndpointState = {
  windowStartMs: number;
  usedInWindow: number;
  usedInSession: number;
};

type EndpointDiagnostics = {
  attempted: number;
  allowed: number;
  blocked: number;
  cacheHits: number;
  errors: number;
};

type ConsumeResult = {
  allowed: boolean;
  reason?: 'minute_budget_exceeded' | 'session_budget_exceeded';
};

const WINDOW_MS = 60_000;
const DEFAULT_BUDGET: BudgetSpec = { perMinute: 4, perSession: 50 };
const BUDGETS: Record<MapboxEndpointFamily, BudgetSpec> = {
  geocode: { perMinute: 18, perSession: 220 },
  search: { perMinute: 14, perSession: 180 },
  searchBox: { perMinute: 6, perSession: 60 },
  tilequery: { perMinute: 3, perSession: 28 },
  directions: { perMinute: 6, perSession: 80 },
  optimization: { perMinute: 1, perSession: 8 },
  matrix: { perMinute: 1, perSession: 8 },
  reverseGeocode: { perMinute: 3, perSession: 25 },
};

const isAggressiveEnabled = (): boolean => {
  const raw = `${process.env.EXPO_PUBLIC_MAPBOX_COST_MODE || ''}`.trim().toLowerCase();
  if (raw === 'off' || raw === 'disabled' || raw === 'false') return false;
  return true;
};

const endpointState: Record<MapboxEndpointFamily, EndpointState> = {
  geocode: { windowStartMs: Date.now(), usedInWindow: 0, usedInSession: 0 },
  search: { windowStartMs: Date.now(), usedInWindow: 0, usedInSession: 0 },
  searchBox: { windowStartMs: Date.now(), usedInWindow: 0, usedInSession: 0 },
  tilequery: { windowStartMs: Date.now(), usedInWindow: 0, usedInSession: 0 },
  directions: { windowStartMs: Date.now(), usedInWindow: 0, usedInSession: 0 },
  optimization: { windowStartMs: Date.now(), usedInWindow: 0, usedInSession: 0 },
  matrix: { windowStartMs: Date.now(), usedInWindow: 0, usedInSession: 0 },
  reverseGeocode: { windowStartMs: Date.now(), usedInWindow: 0, usedInSession: 0 },
};

const diagnostics: Record<MapboxEndpointFamily, EndpointDiagnostics> = {
  geocode: { attempted: 0, allowed: 0, blocked: 0, cacheHits: 0, errors: 0 },
  search: { attempted: 0, allowed: 0, blocked: 0, cacheHits: 0, errors: 0 },
  searchBox: { attempted: 0, allowed: 0, blocked: 0, cacheHits: 0, errors: 0 },
  tilequery: { attempted: 0, allowed: 0, blocked: 0, cacheHits: 0, errors: 0 },
  directions: { attempted: 0, allowed: 0, blocked: 0, cacheHits: 0, errors: 0 },
  optimization: { attempted: 0, allowed: 0, blocked: 0, cacheHits: 0, errors: 0 },
  matrix: { attempted: 0, allowed: 0, blocked: 0, cacheHits: 0, errors: 0 },
  reverseGeocode: { attempted: 0, allowed: 0, blocked: 0, cacheHits: 0, errors: 0 },
};

let lastLoggedAt = 0;
const LOG_INTERVAL_MS = 20_000;

const resetWindowIfNeeded = (endpoint: MapboxEndpointFamily): void => {
  const state = endpointState[endpoint];
  const now = Date.now();
  if (now - state.windowStartMs >= WINDOW_MS) {
    state.windowStartMs = now;
    state.usedInWindow = 0;
  }
};

const maybeLogDiagnostics = (): void => {
  if (!__DEV__) return;
  const now = Date.now();
  if (now - lastLoggedAt < LOG_INTERVAL_MS) return;
  lastLoggedAt = now;
  console.log('[MapboxCostPolicy] usage snapshot', getMapboxCostDiagnostics());
};

export const isAggressiveMapboxCostMode = (): boolean => isAggressiveEnabled();

export const shouldUseLiveRouteEstimates = (): boolean => !isAggressiveEnabled();

export const tryConsumeMapboxBudget = (
  endpoint: MapboxEndpointFamily,
  units: number = 1,
): ConsumeResult => {
  if (!isAggressiveEnabled()) {
    diagnostics[endpoint].attempted += 1;
    diagnostics[endpoint].allowed += 1;
    return { allowed: true };
  }

  const budget = BUDGETS[endpoint] ?? DEFAULT_BUDGET;
  const state = endpointState[endpoint];
  const diag = diagnostics[endpoint];
  resetWindowIfNeeded(endpoint);

  diag.attempted += 1;

  if (state.usedInSession + units > budget.perSession) {
    diag.blocked += 1;
    maybeLogDiagnostics();
    return { allowed: false, reason: 'session_budget_exceeded' };
  }

  if (state.usedInWindow + units > budget.perMinute) {
    diag.blocked += 1;
    maybeLogDiagnostics();
    return { allowed: false, reason: 'minute_budget_exceeded' };
  }

  state.usedInWindow += units;
  state.usedInSession += units;
  diag.allowed += 1;
  maybeLogDiagnostics();
  return { allowed: true };
};

export const recordMapboxCacheHit = (endpoint: MapboxEndpointFamily): void => {
  diagnostics[endpoint].cacheHits += 1;
  maybeLogDiagnostics();
};

export const recordMapboxCostError = (endpoint: MapboxEndpointFamily): void => {
  diagnostics[endpoint].errors += 1;
  maybeLogDiagnostics();
};

export const getMapboxCostDiagnostics = (): Record<
  MapboxEndpointFamily,
  EndpointDiagnostics & {
    perMinuteBudget: number;
    perSessionBudget: number;
    usedInWindow: number;
    usedInSession: number;
  }
> => {
  return {
    geocode: {
      ...diagnostics.geocode,
      perMinuteBudget: BUDGETS.geocode.perMinute,
      perSessionBudget: BUDGETS.geocode.perSession,
      usedInWindow: endpointState.geocode.usedInWindow,
      usedInSession: endpointState.geocode.usedInSession,
    },
    search: {
      ...diagnostics.search,
      perMinuteBudget: BUDGETS.search.perMinute,
      perSessionBudget: BUDGETS.search.perSession,
      usedInWindow: endpointState.search.usedInWindow,
      usedInSession: endpointState.search.usedInSession,
    },
    searchBox: {
      ...diagnostics.searchBox,
      perMinuteBudget: BUDGETS.searchBox.perMinute,
      perSessionBudget: BUDGETS.searchBox.perSession,
      usedInWindow: endpointState.searchBox.usedInWindow,
      usedInSession: endpointState.searchBox.usedInSession,
    },
    tilequery: {
      ...diagnostics.tilequery,
      perMinuteBudget: BUDGETS.tilequery.perMinute,
      perSessionBudget: BUDGETS.tilequery.perSession,
      usedInWindow: endpointState.tilequery.usedInWindow,
      usedInSession: endpointState.tilequery.usedInSession,
    },
    directions: {
      ...diagnostics.directions,
      perMinuteBudget: BUDGETS.directions.perMinute,
      perSessionBudget: BUDGETS.directions.perSession,
      usedInWindow: endpointState.directions.usedInWindow,
      usedInSession: endpointState.directions.usedInSession,
    },
    optimization: {
      ...diagnostics.optimization,
      perMinuteBudget: BUDGETS.optimization.perMinute,
      perSessionBudget: BUDGETS.optimization.perSession,
      usedInWindow: endpointState.optimization.usedInWindow,
      usedInSession: endpointState.optimization.usedInSession,
    },
    matrix: {
      ...diagnostics.matrix,
      perMinuteBudget: BUDGETS.matrix.perMinute,
      perSessionBudget: BUDGETS.matrix.perSession,
      usedInWindow: endpointState.matrix.usedInWindow,
      usedInSession: endpointState.matrix.usedInSession,
    },
    reverseGeocode: {
      ...diagnostics.reverseGeocode,
      perMinuteBudget: BUDGETS.reverseGeocode.perMinute,
      perSessionBudget: BUDGETS.reverseGeocode.perSession,
      usedInWindow: endpointState.reverseGeocode.usedInWindow,
      usedInSession: endpointState.reverseGeocode.usedInSession,
    },
  };
};
